<template>
  <div>
    <helpers-section-header class="mt-6 mb-8"> Trend Signal Rules </helpers-section-header>

    <v-checkbox
      v-if="short"
      v-model="criteria.long_enabled"
      label="Allow Long Signals"
      hide-details
    />
    <v-checkbox
      v-if="short"
      v-model="criteria.short_enabled"
      label="Allow Short Signals"
      hide-details
    />

    <v-checkbox
      v-model="criteria.and_all_rules"
      label="If checked then all trend rules must match a signal, otherwise just one of your rules needs to match"
      hide-details
    />

    <!-- Pricing/Volume Channel Breakout -->
    <helpers-section-sub-header class="mt-8 mb-2">
      Pricing/Volume Channel Breakout
    </helpers-section-sub-header>
    <p>
      A signal is generated when the closing price exceeds the high of the specified time period for
      long positions or falls below the low of the time period for short positions.
    </p>
    <p>
      You can further restrict the signal to only those breakouts which include an increase in avg
      daily volume over a number of days.
    </p>

    <v-data-table
      :headers="pricing_breakout_headers"
      :items="criteria.breakout_rules"
      class="mt-6 mb-8"
    >
      <template #item.increased_volume="{ item }">
        {{ item.increased_volume }}
      </template>
      <template #top>
        <v-dialog v-model="priceDialog" scrollable max-width="500px">
          <template #activator="{ props }">
            <v-btn color="primary" dark class="mb-4" v-bind="props"> New Pricing Breakout </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ pricingBreakoutTitle }}</span>
            </v-card-title>

            <v-card-text style="height: 350px">
              <v-container>
                <v-row density="compact">
                  <v-col cols="12" sm="12">
                    <v-checkbox
                      v-model="editedPrice.enabled"
                      density="compact"
                      label="Rule Enabled"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedPrice.long_short"
                      :items="long_short"
                      label="Signal Type"
                      variant="outlined"
                      hide-details
                      density="compact"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedPrice.high_low"
                      density="compact"
                      :items="high_low"
                      label="New High or New Low"
                      variant="outlined"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedPrice.days"
                      density="compact"
                      :items="days_time_periods"
                      label="# of days for new high/low"
                      variant="outlined"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedPrice.increased_price_percent"
                      density="compact"
                      label="% change in price"
                      hint="optionally set the percentage over the previous high/low the new high/low price must be"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12">
                    <p>
                      Optionally require a previous breakout (up or down) within the past __ days
                      before accepting the signal
                    </p>
                    <p>
                      For example, requiring a previous breakout would let you make the rule: buy
                      Stock A if it makes a 1 week high after having made a 4 week low within the
                      past 10 days. Or buy a 1 week low if it made a 90 day high within the past 10
                      days.
                    </p>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <v-select
                      v-model="editedPrice.previous_breakout_high_low"
                      :items="high_low"
                      label="High or New Low"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <v-select
                      v-model="editedPrice.previous_breakout_days"
                      :items="days_time_periods"
                      label="# of days"
                      hint="Previous breakout  # days"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <v-text-field
                      v-model="editedPrice.previous_breakout_past_days"
                      density="compact"
                      hide-details
                      variant="outlined"
                      hint="breakout occured within the past __ days"
                      label="occurred within # days"
                    />
                  </v-col>
                  <v-col cols="12" sm="12">
                    <v-checkbox
                      v-model="editedPrice.increased_volume"
                      density="compact"
                      label="Must also have increase in trading volume"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedPrice.increased_volume_percent"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="% increase in volume"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedPrice.volume_avg_days"
                      density="compact"
                      hide-details
                      variant="outlined"
                      :items="volume_time_periods"
                      label="Avg Volume Time Period"
                    />
                  </v-col>
                  <v-col v-if="1 == 2" cols="12" sm="6">
                    <v-text-field
                      v-model="editedPrice.allocation"
                      density="compact"
                      label="% Allocated to Signal"
                      hint="% of total trend signal resources devoted to this breakout model"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <div class="flex-grow-1" />
              <v-btn color="blue-darken-1" text @click="closePrice"> Cancel </v-btn>
              <v-btn color="blue-darken-1" text @click="savePrice"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template #item.action="{ item }">
        <v-icon small class="mr-2" @click="editPrice(item)"> edit </v-icon>
        <v-icon small @click="deletePrice(item)"> delete </v-icon>
      </template>
      <template #item.previous_breakout_past_days="{ item }">
        <div
          v-if="
            item.previous_breakout_past_days &&
            item.previous_breakout_high_low &&
            item.previous_breakout_days
          "
        >
          Previous {{ item.previous_breakout_days }} day
          {{ item.previous_breakout_high_low }} breakout within the past
          {{ item.previous_breakout_past_days }} days
        </div>
      </template>
      <template #item.enabled="{ item }">
        <v-switch v-model="item.enabled" color="primary" />
      </template>
      <template #no-data> No pricing breakout signals configured </template>
    </v-data-table>

    <!-- Find Stocks Based on Perf vs S&P-->
    <helpers-section-sub-header class="mt-8 mb-2">
      Find Stocks Based on Perf vs S&amp;P
    </helpers-section-sub-header>
    <p>
      Only include stocks that are either up or down vs the S&amp;P 500 in the past [1,3,6,9,12]
      months
    </p>

    <v-data-table :headers="sp_headers" :items="criteria.stocks_vs_sp" class="mt-6 mb-8">
      <template #top>
        <v-dialog v-model="spDialog" scrollable max-width="600px">
          <template #activator="{ props }">
            <v-btn color="primary" dark class="mb-4" v-bind="props"> New Rule </v-btn>
          </template>
          <v-card>
            <v-card-title class="ma-0">
              <span class="text-h6 ma-0">{{ spTitle }}</span>
            </v-card-title>
            <v-card-text v-show="editedSp.type" style="height: 380px">
              <v-btn size="small" @click="editedSp.type = null"> Use Old Rules Instead </v-btn>

              <v-radio-group v-model="editedSp.type" class="mt-0">
                <v-row density="compact">
                  <v-col cols="12">
                    <v-checkbox
                      v-model="editedSp.enabled"
                      density="compact"
                      variant="outlined"
                      label="Rule Enabled"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-row density="compact">
                      <v-col cols="12" sm="2" md="1">
                        <v-radio :value="1" />
                      </v-col>
                      <v-col cols="12" sm="10">
                        Only buy stocks that have <strong>under</strong>performed the S&amp;P
                      </v-col>
                      <v-col cols="12" sm="12" md="7">
                        <v-select
                          v-model="editedSp.months"
                          density="compact"
                          hide-details
                          variant="outlined"
                          :items="stocks_vs_sp_months"
                          :disabled="editedSp.type != 1"
                          item-title="text"
                          item-value="value"
                          prefix="over the past"
                          label="Trading days"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percentage_points"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix="by at least"
                          :disabled="editedSp.type != 1"
                          label=""
                          suffix="percentage points"
                          placeholder=""
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percentage_points_max"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix=""
                          :disabled="editedSp.type != 1"
                          label="optional max"
                          placeholder="optional max"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12">
                    <v-row density="compact">
                      <v-col cols="12" sm="2" md="1">
                        <v-radio :value="2" />
                      </v-col>
                      <v-col cols="12" sm="10">
                        Only buy stocks that have <strong>out</strong>performed the S&amp;P
                      </v-col>
                      <v-col cols="12" sm="12" md="7">
                        <v-select
                          v-model="editedSp.months"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix="over the past"
                          :items="stocks_vs_sp_months"
                          item-title="text"
                          :disabled="editedSp.type != 2"
                          item-value="value"
                          label="Trading days"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percentage_points"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix="by at least"
                          :disabled="editedSp.type != 2"
                          label=""
                          suffix="percentage points"
                          placeholder=""
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percentage_points_max"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix=""
                          :disabled="editedSp.type != 2"
                          label="optional max"
                          placeholder="optional max"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12">
                    <v-row density="compact">
                      <v-col cols="12" sm="2" md="1">
                        <v-radio :value="3" />
                      </v-col>
                      <v-col cols="12" sm="10">
                        Only buy stocks that have <strong>under</strong>performed the S&amp;P
                      </v-col>
                      <v-col cols="12" sm="12" md="7">
                        <v-select
                          v-model="editedSp.months"
                          density="compact"
                          hide-details
                          variant="outlined"
                          :items="stocks_vs_sp_months"
                          item-title="text"
                          :disabled="editedSp.type != 3"
                          item-value="value"
                          prefix="over the past"
                          label="Trading days"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percent_threshold"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix="by at least"
                          label=""
                          :disabled="editedSp.type != 3"
                          suffix="percent"
                          placeholder=""
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percent_threshold_max"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix=""
                          :disabled="editedSp.type != 3"
                          label="optional max"
                          placeholder="optional max"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12">
                    <v-row density="compact">
                      <v-col cols="12" sm="2" md="1">
                        <v-radio :value="4" />
                      </v-col>
                      <v-col cols="12" sm="10">
                        Only buy stocks that have <strong>out</strong>performed the S&amp;P
                      </v-col>
                      <v-col cols="12" sm="12" md="7">
                        <v-select
                          v-model="editedSp.months"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix="over the past"
                          :items="stocks_vs_sp_months"
                          item-title="text"
                          item-value="value"
                          :disabled="editedSp.type != 4"
                          label="Trading days"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percent_threshold"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix="by at least"
                          label=""
                          suffix="percent"
                          placeholder=""
                          :disabled="editedSp.type != 4"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="6">
                        <v-text-field
                          v-model="editedSp.percent_threshold_max"
                          density="compact"
                          hide-details
                          variant="outlined"
                          prefix=""
                          :disabled="editedSp.type != 4"
                          label="optional max"
                          placeholder="optional max"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-card-text>
            <v-card-text v-show="!editedSp.type" style="height: 380px">
              <v-container>
                <v-row density="compact">
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedSp.long_short"
                      density="compact"
                      variant="outlined"
                      :items="long_short"
                      label="Signal Type"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedSp.months"
                      density="compact"
                      hide-details
                      variant="outlined"
                      :items="stocks_vs_sp_months"
                      item-title="text"
                      item-value="value"
                      label="Trading days"
                    />
                  </v-col>
                  <v-col v-if="1 == 2" cols="12" sm="6">
                    <v-select
                      v-model="editedSp.up_or_down"
                      density="compact"
                      hide-details
                      variant="outlined"
                      :items="stocks_vs_sp_up_down"
                      label="Up or Down"
                      single-line
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedSp.percent_threshold"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="Performance vs S&amp;P % Min."
                      placeholder="Min. Performance %"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedSp.percent_threshold_max"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="Performance vs S&amp;P % Max"
                      placeholder="Max. Performance %"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedSp.consecutive_days"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="Consecutive days of outperformance"
                    />
                  </v-col>
                </v-row>
                <v-row density="compact">
                  <v-col cols="12" sm="12" md="6">
                    <v-text-field
                      v-model="editedSp.plus_minus"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="Statistically Significant: Min."
                    />
                  </v-col>

                  <v-col cols="12" sm="12" md="6">
                    <v-text-field
                      v-model="editedSp.plus_minus_max"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="Statistically Significant: Max."
                    />
                  </v-col>

                  <v-col v-if="1 == 2" cols="12" sm="6">
                    <v-text-field
                      v-model="editedSp.allocation"
                      density="compact"
                      hide-details
                      label="% Allocated to Signal"
                      hint="% of total trend signal resources devoted to this breakout model"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <div class="flex-grow-1" />
              <v-btn color="blue-darken-1" text @click="closeSp"> Cancel </v-btn>
              <v-btn color="blue-darken-1" text @click="saveSp"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template #item.enabled="{ item }">
        <v-switch v-model="item.enabled" color="primary" />
      </template>
      <template #item.type="{ item }">
        <span v-if="item.type == 1"
          >Only buy stocks that have underperformed the S&amp;P over {{ item.months }} months:
          {{ item.percentage_points }}
          {{ item.percentage_points_max ? ' to ' + item.percentage_points_max : '' }}
          percentage points
        </span>
        <span v-if="item.type == 2"
          >Only buy stocks that have outperformed the S&amp;P over {{ item.months }} months:
          {{ item.percentage_points }}
          {{ item.percentage_points_max ? ' to ' + item.percentage_points_max : '' }}
          percentage points
        </span>
        <span v-if="item.type == 3"
          >Only buy stocks that have underperformed the S&amp;P over {{ item.months }} months:
          {{ item.percent_threshold }}
          {{ item.percent_threshold_max ? ' to ' + item.percent_threshold_max : '' }}
          percent
        </span>
        <span v-if="item.type == 4"
          >Only buy stocks that have outperformed the S&amp;P over {{ item.months }} months:
          {{ item.percent_threshold }}
          {{ item.percent_threshold_max ? ' to ' + item.percent_threshold_max : '' }}
          percent
        </span>
        <span v-if="!item.type"
          >Old Rule: {{ item.months }} months, Perf min: {{ item.percent_threshold }}, Perf max:
          {{ item.percent_threshold_max }}, significance min: {{ item.plus_minus }}, significance
          max: {{ item.plus_minus_max }}
        </span>
      </template>

      <template #item.action="{ item }">
        <v-icon small class="mr-2" @click="editSp(item)"> edit </v-icon>
        <v-icon small @click="deleteSp(item)"> delete </v-icon>
      </template>
      <template #no-data> No rules configured </template>
    </v-data-table>

    <!-- Pricing/Volume Channel Breakout -->
    <helpers-section-sub-header class="mt-8 mb-2">
      Moving Average Trends
    </helpers-section-sub-header>
    <p>
      Only buy a stock if it's ### day moving average is above or below it's ### day moving average
      by at least ### ATRs.
    </p>

    <v-data-table :headers="ma_headers" :items="criteria.moving_average_rules" class="mt-6 mb-8">
      <template #top>
        <v-dialog v-model="maDialog" scrollable max-width="450px">
          <template #activator="{ props }">
            <v-btn color="primary" dark class="mb-4" v-bind="props">
              New Moving Average Rule
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ maTitle }}</span>
            </v-card-title>

            <v-card-text style="height: 350px">
              <v-container>
                <v-row density="compact">
                  <v-col cols="12" sm="12">
                    <v-checkbox v-model="editedMa.enabled" density="compact" label="Rule Enabled" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedMa.long_short"
                      :items="long_short"
                      label="Signal Type"
                      hint="Long or Short Signal"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedMa.days_ma1"
                      :items="ma_days"
                      label="1st Moving Avg Days"
                      hint="Moving Average"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedMa.above_below"
                      :items="above_below"
                      label="above or below"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedMa.days_ma2"
                      :items="ma_days"
                      label="2nd Moving Avg Days"
                      hint="Moving Average"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editedMa.atr"
                      density="compact"
                      label="# ATRs"
                      hint="MA 1 must be above or below MA 2 by at least ### ATRs (4-week)"
                      variant="outlined"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <div class="flex-grow-1" />
              <v-btn color="blue-darken-1" text @click="closeMa"> Cancel </v-btn>
              <v-btn color="blue-darken-1" text @click="saveMa"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template #item.action="{ item }">
        <v-icon small class="mr-2" @click="editMa(item)"> edit </v-icon>
        <v-icon small @click="deleteMa(item)"> delete </v-icon>
      </template>

      <template #item.enabled="{ item }">
        <v-switch v-model="item.enabled" />
      </template>
      <template #no-data> No moving average signals configured </template>
    </v-data-table>

    <!-- Overbought -->
    <helpers-section-sub-header class="mt-8 mb-2">
      Short-Term Overbought Trends
    </helpers-section-sub-header>
    <p>Short-term overbought in a longer term uptrend may suggest further growth ahead.</p>

    <v-data-table
      :headers="overbought_headers"
      :items="criteria.overbought_rules"
      class="mt-6 mb-8"
    >
      <template #top>
        <v-dialog v-model="overboughtDialog" scrollable max-width="450px">
          <template #activator="{ props }">
            <v-btn color="primary" dark class="mb-4" v-bind="props">
              New Short-Term Overbought Rule
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ overboughtTitle }}</span>
            </v-card-title>

            <v-card-text style="height: 350px">
              <p>
                Enter the number of consecutive days the stock has closed higher along with the
                daily moving average the closing price must be above to qualify
              </p>
              <v-container>
                <v-row density="compact">
                  <v-col cols="12" sm="12">
                    <v-checkbox
                      v-model="editedOverbought.enabled"
                      density="compact"
                      label="Rule Enabled"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedOverbought.long_short"
                      :items="long_short"
                      label="Signal Type"
                      hint="Long or Short Signal"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedOverbought.consecutive_days"
                      :items="ma_days"
                      label="Consecutive Days Outperform"
                      hint="Number of Days"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedOverbought.ma_days"
                      :items="ma_days"
                      label="Moving Avg Days"
                      hint="Closing Price above the daily moving average specified"
                      density="compact"
                      variant="outlined"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <div class="flex-grow-1" />
              <v-btn color="blue-darken-1" text @click="closeOverbought"> Cancel </v-btn>
              <v-btn color="blue-darken-1" text @click="saveOverbought"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template #item.action="{ item }">
        <v-icon small class="mr-2" @click="editOverbought(item)"> edit </v-icon>
        <v-icon small @click="deleteOverbought(item)"> delete </v-icon>
      </template>

      <template #item.enabled="{ item }">
        <v-switch v-model="item.enabled" />
      </template>
      <template #no-data> No moving overbought signals configured </template>
    </v-data-table>

    <!-- Avg 13F Ranking Breakout -->
    <helpers-section-sub-header class="mt-8 mb-2">
      Avg 13F Ranking Breakout
    </helpers-section-sub-header>
    <p>Only buy a stock if it's average 13F ranking changes</p>
    <v-btn
      color="primary"
      dark
      class="mb-4"
      @click="show_avg_ranking(null, criteria.avg_ranking_rules)"
    >
      New Avg Ranking Rule
    </v-btn>
    <v-data-table
      :headers="avg_ranking_headers"
      :items="criteria.avg_ranking_rules"
      class="mt-6 mb-8"
    >
      <template #item.direction="{ item }">
        <div v-if="item.direction == 1">
          Avg. 13F Ranking is &gt;= the lowest Avg. 13F Ranking of the past
          {{ item.number_of_quarters }} quarters.
        </div>
        <div v-else>
          Avg. 13F Ranking is &lt;= the lowest Avg. 13F Ranking of the past
          {{ item.number_of_quarters }} quarters.
        </div>
        <span v-if="item.change_min && item.change_min.trim().length > 0">
          minimum % change of {{ item.change_min }}% from prior quarter.
        </span>

        <span
          v-if="
            item.ranking_min &&
            item.ranking_min.trim().length > 0 &&
            item.ranking_max &&
            item.ranking_max.trim().length > 0
          "
        >
          Avg Ranking between {{ item.ranking_min }} and {{ item.ranking_max }}.
        </span>
        <span v-else-if="item.ranking_min && item.ranking_min.trim().length > 0">
          Avg Ranking &gt;= {{ item.ranking_min }}.
        </span>
        <span v-else-if="item.ranking_max && item.ranking_max.trim().length > 0">
          Avg Ranking &lt;= {{ item.ranking_max }}.
        </span>
      </template>
      <template #item.action="{ item }">
        <v-icon small class="mr-2" @click="show_avg_ranking(item, criteria.avg_ranking_rules)">
          edit
        </v-icon>
        <v-icon small @click="delete_avg_ranking(item, criteria.avg_ranking_rules)">
          delete
        </v-icon>
      </template>

      <template #item.enabled="{ item }">
        <v-switch v-model="item.enabled" />
      </template>
      <template #no-data> No Avg Ranking Breakouts configured </template>
    </v-data-table>
    <avg-ranking-dialog ref="avg_ranking_dialog" />
    <trend-kama />
    <trend-bollinger />
    <trend-adx />
    <trend-high-low />
  </div>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'
import AvgRankingDialog from '../_AvgRankingBreakout'
import TrendKama from './_TrendKama'
import TrendBollinger from './_TrendBollinger'
import TrendAdx from './_TrendAdx'
import TrendHighLow from './_TrendHighLow'

export default {
  components: {
    AvgRankingDialog,
    TrendKama,
    TrendBollinger,
    TrendAdx,
    TrendHighLow
  },
  data() {
    return {
      ma_days: [],
      short: false,
      priceDialog: false,
      spGroup: null,
      maDialog: false,
      overboughtDialog: false,
      spDialog: false,
      dialog: false,
      stocks_vs_sp_up_down: ['up', 'down'],
      high_low: ['new high', 'new low'],
      above_below: ['above', 'below'],
      long_short: ['long', 'short'],
      stocks_vs_sp_months: [
        { text: '20 trading days', value: 1 },
        { text: '60 trading days', value: 3 },
        { text: '120 trading days', value: 6 },
        { text: '180 trading days', value: 9 },
        { text: '240 trading days', value: 12 },
        { text: '480 trading days', value: 24 }
      ],
      volume_time_periods: [
        { text: '1 week', value: 7 },
        { text: '2 weeks', value: 14 },
        { text: '3 weeks', value: 21 },
        { text: '4 weeks', value: 28 },
        { text: '90 Days', value: 90 }
      ],
      days_time_periods: [
        { text: '1 week', value: 7 },
        { text: '2 weeks', value: 14 },
        { text: '3 weeks', value: 21 },
        { text: '4 weeks', value: 28 },
        { text: '50 days', value: 50 },
        { text: '90 days', value: 90 },
        { text: '6-months', value: 180 },
        { text: '1 Year', value: 365 }
      ],
      ma_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Rule Type',
          align: 'left',
          sortable: true,
          key: 'long_short'
        },
        {
          title: 'Moving Avg 1',
          align: 'left',
          sortable: true,
          key: 'days_ma1'
        },
        {
          title: 'Above or Below',
          align: 'left',
          sortable: true,
          key: 'above_below'
        },
        {
          title: 'Moving Avg 2',
          align: 'left',
          sortable: true,
          key: 'days_ma2'
        },
        {
          title: 'ATR',
          align: 'left',
          sortable: true,
          key: 'atr'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      overbought_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Rule Type',
          align: 'left',
          sortable: true,
          key: 'long_short'
        },
        {
          title: 'Consecutive Days',
          align: 'left',
          sortable: true,
          key: 'consecutive_days'
        },
        {
          title: 'Moving Avg Days',
          align: 'left',
          sortable: true,
          key: 'ma_days'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      pricing_breakout_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Rule Type',
          align: 'left',
          sortable: true,
          key: 'long_short'
        },
        {
          title: 'Price Direction',
          align: 'left',
          sortable: true,
          key: 'high_low'
        },
        {
          title: 'Number of days',
          align: 'left',
          sortable: true,
          key: 'days'
        },
        { title: '% increase/decrease in price', key: 'increased_price_percent' },
        { title: 'Previous Breakout Required', key: 'previous_breakout_past_days' },
        { title: 'Must have increase in volume', key: 'increased_volume' },
        { title: 'Avg Daily Volume # of Days', key: 'volume_avg_days' },
        { title: '% increase in volume', key: 'increased_volume_percent' },
        //  { title: '% Allocated to Signal', key: 'allocation' },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      editedMa: {
        enabled: true,
        above_below: 'above',
        days_ma1: 50,
        days_ma2: 200,
        atr: 2,
        long_short: 'long'
      },
      defaultMa: {
        enabled: true,
        above_below: 'above',
        days_ma1: 50,
        days_ma2: 200,
        atr: 2,
        long_short: 'long'
      },
      editedOverbought: { enabled: true, consecutive_days: 5, ma_days: 200, long_short: 'long' },
      defaultOverbought: { enabled: true, consecutive_days: 5, ma_days: 200, long_short: 'long' },
      editedIndex: -1,
      editedPrice: {
        long_short: 'long',
        high_low: 'high',
        days: 0,
        allocation: 0,
        increased_price_percent: 0,
        increased_volume: false,
        increased_volume_percent: 0,
        volume_avg_days: 0,
        previous_breakout_high_low: null,
        previous_breakout_days: null,
        previous_breakout_past_days: null,
        enabled: true
      },
      defaultPrice: {
        long_short: 'long',
        high_low: 'high',
        days: 20,
        allocation: 100,
        increased_price_percent: 0,
        increased_volume: false,
        increased_volume_percent: 5.0,
        volume_avg_days: 0,
        previous_breakout_high_low: null,
        previous_breakout_days: null,
        previous_breakout_past_days: null,
        enabled: true
      },
      editedSp: {
        type: 1,
        percentage_points: 3,
        percentage_points_max: null,
        long_short: 'long',
        percent_threshold: 10.0,
        percent_threshold_max: null,
        months: 3,
        up_or_down: 'down',
        plus_minus: null,
        plus_minus_max: null,
        allocation: 100,
        consecutive_days: null,
        enabled: true
      },
      defaultSp: {
        type: 1,
        percentage_points: 3,
        percentage_points_max: null,
        long_short: 'long',
        percent_threshold: 10.0,
        percent_threshold_max: null,
        months: 3,
        up_or_down: 'down',
        plus_minus: null,
        plus_minus_max: null,
        allocation: 100,
        consecutive_days: null,
        enabled: true
      },

      sp_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Rule Type',
          align: 'left',
          sortable: true,
          key: 'type'
        },

        { title: 'Actions', key: 'action', sortable: false }
      ],
      avg_ranking_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Rule Type',
          align: 'left',
          sortable: true,
          key: 'direction'
        },

        { title: 'Actions', key: 'action', sortable: false }
      ]
    }
  },

  computed: {
    pricingBreakoutTitle() {
      return this.editedIndex === -1 ? 'New Pricing Breakout' : 'Edit Pricing Breakout'
    },
    spTitle() {
      return this.editedIndex === -1 ? 'New Rule' : 'Edit Rule'
    },
    maTitle() {
      return this.editedIndex === -1 ? 'New Rule' : 'Edit Rule'
    },
    overboughtTitle() {
      return this.editedIndex === -1 ? 'New Rule' : 'Edit Rule'
    },
    ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.trend_signal',
      enabled: 'app.whaleTraderCriteria.signals.trend_signal.enabled',
      signalView: 'app.signalView',
      exit_time_periods: 'app.exit_time_periods'
    })
  },

  watch: {
    priceDialog(val) {
      val || this.closePrice()
    },
    spDialog(val) {
      val || this.closeSp()
    },
    maDialog(val) {
      val || this.closeMa()
    },
    overboughtDialog(val) {
      val || this.closeOverbought()
    }
  },

  created() {
    this.initializePrice()
    for (var i = 1; i < 71; i++) {
      this.ma_days.push(i)
    }
    for (i = 75; i < 301; i += 5) {
      this.ma_days.push(i)
    }
  },

  methods: {
    initializePrice() {},
    show_avg_ranking(item) {
      this.$refs.avg_ranking_dialog.show(item, this.criteria.avg_ranking_rules)
    },
    delete_avg_ranking(item) {
      this.$refs.avg_ranking_dialog.deleteRanking(item, this.criteria.avg_ranking_rules)
    },

    editPrice(item) {
      this.editedIndex = this.criteria.breakout_rules.indexOf(item)
      this.editedPrice = Object.assign({}, item)
      this.priceDialog = true
    },

    deletePrice(item) {
      const index = this.criteria.breakout_rules.indexOf(item)
      confirm('Are you sure you want to delete this price?') &&
        this.criteria.breakout_rules.splice(index, 1)
    },

    closePrice() {
      this.priceDialog = false
      setTimeout(() => {
        this.editedPrice = Object.assign({}, this.defaultPrice)
        this.editedIndex = -1
      }, 300)
    },

    savePrice() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.breakout_rules[this.editedIndex], this.editedPrice)
      } else {
        this.criteria.breakout_rules.push(this.editedPrice)
      }
      this.closePrice()
    },

    editSp(item) {
      this.editedIndex = this.criteria.stocks_vs_sp.indexOf(item)
      this.editedSp = Object.assign({}, item)
      this.spDialog = true
    },

    deleteSp(item) {
      const index = this.criteria.stocks_vs_sp.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.criteria.stocks_vs_sp.splice(index, 1)
    },

    closeSp() {
      this.spDialog = false
      setTimeout(() => {
        this.editedSp = Object.assign({}, this.defaultSp)
        this.editedIndex = -1
      }, 300)
    },

    saveSp() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.stocks_vs_sp[this.editedIndex], this.editedSp)
      } else {
        this.criteria.stocks_vs_sp.push(this.editedSp)
      }
      this.closeSp()
    },
    editMa(item) {
      this.editedIndex = this.criteria.moving_average_rules.indexOf(item)
      this.editedMa = Object.assign({}, item)
      this.maDialog = true
    },

    deleteMa(item) {
      const index = this.criteria.moving_average_rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.criteria.moving_average_rules.splice(index, 1)
    },

    closeMa() {
      this.maDialog = false
      setTimeout(() => {
        this.editedMa = Object.assign({}, this.defaultMa)
        this.editedIndex = -1
      }, 300)
    },

    saveMa() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.moving_average_rules[this.editedIndex], this.editedMa)
      } else {
        this.criteria.moving_average_rules.push(this.editedMa)
      }
      this.closeMa()
    },
    editOverbought(item) {
      this.editedIndex = this.criteria.overbought_rules.indexOf(item)
      this.editedOverbought = Object.assign({}, item)
      this.overboughtDialog = true
    },

    deleteOverbought(item) {
      const index = this.criteria.overbought_rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.criteria.overbought_rules.splice(index, 1)
    },

    closeOverbought() {
      this.overboughtDialog = false
      setTimeout(() => {
        this.editedOverbought = Object.assign({}, this.defaultOverbought)
        this.editedIndex = -1
      }, 300)
    },

    saveOverbought() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.overbought_rules[this.editedIndex], this.editedOverbought)
      } else {
        if (!this.criteria.overbought_rules) {
          this.criteria.overbought_rules = []
        }
        this.criteria.overbought_rules.push(this.editedOverbought)
      }
      this.closeOverbought()
    }
  }
}
</script>
